import React, {FormEvent, useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import intl from 'react-intl-universal';
import {useAppState, useRootStore, useAuthService} from "@stores/AppStateContext";

import {AWSAPIError} from "../../../../../utils/types";

import {MaterialUiInput} from "../../../../components/form/material-ui-input/MaterialUiInput";
import {Button} from "../../../../components/Button";
import {ResendForgetPasswordCode} from "./ResendForgetPasswordCode";

import {AuthModal, AuthModalHeader, logIn} from "../AuthModal";

import {MODALS} from '../../modal-names';
import {inAppEventsHub} from "../../../../../volatile/InAppEventsHub";

interface FormData {
    newPassword: { value: string };
    confirm_code: { value: string };
}

interface FormErrors {
    email?: string;
    newPassword?: string;
    confirm_code?: string;
}

export const ForgotPassword = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const authService = useAuthService();
    const activeModal = appState.topModal;
    const [email, setEmail] = useState('');
    const [step, setStep] = useState(1);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrors({});
        const {confirm_code, newPassword} = event.target as typeof event.target & FormData;

        if (step === 1) {
            setIsProcessing(true);
            try {
                await authService.resetPassword(email);
                setIsProcessing(false);
                setStep(2);
            } catch (error: unknown) {
                setIsProcessing(false);
                if ((error as Error).message) {
                    switch ((error as AWSAPIError).name) {
                        case 'UserNotFoundException':
                        case 'InvalidParameterException': {
                            setErrors({
                                email: intl.get('aws_api_error.InvalidParameterException.no_confirmed_account_found').d('Cannot reset password for the user as there is no registered or verified email.')
                            });
                            break;
                        }
                        default:
                            setErrors({
                                email: (error as Error).message
                            });
                    }
                }
            }
        } else {
            setIsProcessing(true);
            try {
                await authService.confirmResetPassword(email, confirm_code.value, newPassword.value);
                await logIn(appState, rootStore.view, email, newPassword.value);
                setIsProcessing(false);
            } catch (error: unknown) {
                setIsProcessing(false);
                switch ((error as AWSAPIError).name) {
                    case 'CodeMismatchException': {
                        setErrors({
                            confirm_code: intl.get('aws_api_error.CodeMismatchException').d('Invalid verification code provided, please try again.')
                        });
                        break;
                    }
                    case 'InvalidPasswordException': {
                        setErrors({
                            newPassword: intl.get('aws_api_error.InvalidPasswordException').d('Password field must contain at least 8 symbols.'),
                        });
                        break;
                    }
                    case 'UserNotFoundException': {
                        setErrors({
                            email: intl.get('aws_api_error.reset_password.UserNotFoundException').d("The email you entered doesn't match our records. Please double-check and try again"),
                        });
                        break;
                    }
                    default:
                        setErrors({
                            newPassword: (error as Error).message
                        });
                }
            }
        }
    }

    const handleBack = useCallback(() => {
        rootStore.view.openPageByName('sign-in');
    }, [setStep]);

    useEffect(() => {
        inAppEventsHub.subscribe('AuthBack', handleBack);
        return () => {
            inAppEventsHub.unsubscribe('AuthBack', handleBack);
        }
    }, [handleBack]);

    return (
        <AuthModal
            isOpen={activeModal === MODALS.FORGOT_PASSWORD}
            onClose={() => {
            }}
            isBackVisible={true}
        >
            <form onSubmit={handleSubmit} autoComplete='off'>
                <AuthModalHeader title={intl.get('login.restore_password').d('Reset password')}/>
                {
                    step === 1
                        ? (
                            <>
                                <div className='AuthModalBodyStart body-large'>
                                    {intl.getHTML('reset_password.info_message').d(`<b>Enter your email</b> and <b>we'll send you confirmation code</b> to reset your password.`)}
                                </div>
                                <MaterialUiInput
                                    key={'email'}
                                    className={'AuthModalBodyStart Auth-input'}
                                    name="email"
                                    isRequired={true}
                                    fullWidth={true}
                                    label={intl.get('sign_up.email').d('Email')}
                                    value={email}
                                    type={'email'}
                                    onChange={setEmail}
                                    error={errors.email}
                                    emptyFieldError={intl.get('email.empty.field').d("Please provide your email")}
                                />
                                <Button
                                    type="primary"
                                    className="Auth-input Auth-btn"
                                    htmlType="submit"
                                    fullWidth
                                    text={intl.get('login.request_confirmation_code').d('Request confirmation code')}
                                    loading={isProcessing}
                                    size={'lg'}
                                />
                            </>
                        )
                        : (
                            <>
                                <div className="AuthModalBodyStart Auth-confirmation-message">
                                    <div
                                        className='body-large-bold'>{intl.get('Auth-confirmation-message-header').d('Check your email')}</div>
                                    <div className='Auth-confirmation-message-header body-large'>
                                        {intl.getHTML('reset_password.confirm_note', {your_mail: email}).d(`We've sent a confirmation code to <b>{your_mail}</b>. Please enter the code to update your password.`)}
                                    </div>
                                </div>
                                <div className="AuthModalBodyStart Auth-text-line label-large">
                                    {intl.get('sign_up.resend_message').d("Didn't receive code?")}
                                    <ResendForgetPasswordCode
                                        setErrors={setErrors}
                                        getEmail={() => email}
                                    />
                                </div>
                                <MaterialUiInput
                                    key="confirmField"
                                    className={'Auth-small-margin Auth-input'}
                                    isRequired
                                    name="confirm_code"
                                    autoComplete={"off"}
                                    label={intl.get('sign_up.confirm_label').d('Confirmation code')}
                                    fullWidth
                                    error={errors.confirm_code}
                                />
                                <MaterialUiInput
                                    key={'new-password'}
                                    isRequired={true}
                                    className={'Auth-input'}
                                    name="newPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    showPassword={showNewPassword}
                                    setShowPassword={setShowNewPassword}
                                    label={intl.get('sign_up.new_password').d('New password')}
                                    fullWidth={true}
                                    error={errors.newPassword}
                                />

                                <Button
                                    type="primary"
                                    className="Auth-input Auth-btn"
                                    htmlType="submit"
                                    fullWidth
                                    text={intl.get('login.update_password').d('Update password')}
                                    loading={isProcessing}
                                    size={'lg'}
                                />
                            </>
                        )
                }
            </form>
        </AuthModal>
    );
})
